<script setup lang="ts">
import { computed, onBeforeMount, watch, Ref, ref } from "vue";
import { DeviceDisplayType } from "../../enums";
import { ISectionComponent } from "../../interfaces";
import { useRenderStore } from "../../stores/renderStore";

const renderStore = useRenderStore();

const props = defineProps(["component"]);
const component = ref(props.component) as Ref<ISectionComponent>;

const sectionBgImage = ref(component.value.customData.backgroundImage);
const sectionBgColor = ref(component.value.styles.backgroundColor);
const sectionPaddingBottom = ref(component.value.customData.bottomPadding);
const sectionPaddingTop = ref(component.value.customData.topPadding);
const sectionSpacingSize = ref(component.value.customData.spacingSize);
const sectionContainerSize = ref(component.value.customData.containerSize);
const isMobile = ref(false);

const showSection = computed(() => {
    if (renderStore.builderMode) return true;

    if (component.value.customData.deviceDisplay === DeviceDisplayType.All) {
        return true;
    }
    else if (component.value.customData.deviceDisplay === DeviceDisplayType.Mobile && isMobile.value) {
        return true;
    }
    else if (component.value.customData.deviceDisplay === DeviceDisplayType.Desktop && !isMobile.value) {
        return true;
    }

    return false;
});

const updateBackgroundImage = () => {
    if (isMobile.value && component.value.customData.useMobileBackgroundImage) {
        sectionBgImage.value = component.value.customData.mobileBackgroundImage;
    } else {
        sectionBgImage.value = component.value.customData.backgroundImage;
    }
};

const updateIsMobile = () => {
    isMobile.value = window.innerWidth < 768;
    updateBackgroundImage();
};

window.addEventListener("resize", updateIsMobile);

onBeforeMount(() => {
    if (component.value.customData.useMobileBackgroundImage) {
        updateIsMobile();
    }
});

watch(() => component.value.styles.backgroundColor, () => {
    sectionBgColor.value = component.value.styles.backgroundColor;
});

watch(() => component.value.customData.backgroundImage, () => {
    sectionBgImage.value = component.value.customData.backgroundImage;
});

watch(() => component.value.customData.spacingSize, () => {
    sectionSpacingSize.value = component.value.customData.spacingSize;
});

watch(() => component.value.customData.containerSize, () => {
    sectionContainerSize.value = component.value.customData.containerSize;
});

watch(() => component.value.customData.bottomPadding, () => {
    sectionPaddingBottom.value = component.value.customData.bottomPadding;
});

watch(() => component.value.customData.topPadding, () => {
    sectionPaddingTop.value = component.value.customData.topPadding;
});
</script>

<template>
    <template v-if="renderStore.hasOneSectionAndIsModalEmbed">
        <slot name="children"></slot>
    </template>
    <template v-else>
        <section v-if="showSection" class="section-container" :class="[component.styles.cssClass,
        sectionSpacingSize && `spacing-${sectionSpacingSize}`]" :style="[
            sectionBgImage ? { backgroundImage: `url(${sectionBgImage})` } : '',
            sectionBgColor ? { backgroundColor: sectionBgColor } : '',
            sectionPaddingBottom ? { 'padding-bottom': sectionPaddingBottom + 'px', } : '',
            sectionPaddingTop ? { 'padding-top': sectionPaddingTop + 'px', } : ''
        ]">
            <div v-if="component.styles.fullWidth" class="layout-row layout-row-full">
                <slot name="children"></slot>
            </div>

            <div v-else :class="sectionContainerSize ? `layout-container-${sectionContainerSize}` : 'layout-container'">

                <div class="layout-row">
                    <slot name="children"></slot>
                </div>
            </div>
        </section>
    </template>
</template>